import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Avatar,
  Chip,
  InputAdornment,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  Mic as MicIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import conversationService from '../services/conversationService';
import MessageBubble from '../components/MessageBubble';
import { formatDateArgentina, getDateLabel } from '../utils/timezone';
import { getPhoneCountryInfo } from '../utils/phoneUtils';
import ParticipantSider from '../components/ParticipantSider';

const ConversationDetailNew = () => {
  const { sessionId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [unreadNewMessages, setUnreadNewMessages] = useState(0); // Counter for unread new messages
  const lastMessageTimestampRef = useRef(null); // Track the timestamp of the last message we have
  const [isSiderOpen, setIsSiderOpen] = useState(false);
  const [isPhoneMode, setIsPhoneMode] = useState(false); // Track if we're viewing by phone


  useEffect(() => {
    if (sessionId) {
      setUnreadNewMessages(0); // Resetear contador al cambiar de conversación
      loadConversation();
    }
  }, [sessionId]); // Solo depende de sessionId

  // Separate effect for auto-refresh of messages
  useEffect(() => {
    if (!sessionId) {
      console.log('Message update interval not started: no sessionId');
      return;
    }

    // Skip auto-refresh for phone mode (would need different logic)
    if (isPhoneMode) {
      console.log('Auto-refresh disabled for phone mode');
      return;
    }

    console.log('Starting message update interval for session:', sessionId);
    
    // Auto-refresh cada 10 segundos
    const interval = setInterval(async () => {
      try {
        // If we have a timestamp from the last message, search from there
        // If not, search for messages from the last 2 minutes
        const checkTime = lastMessageTimestampRef.current 
          ? new Date(lastMessageTimestampRef.current)
          : new Date(Date.now() - 120000); // 2 minutos atrás si no hay referencia
        
        console.log(`[Session: ${sessionId}] Checking for message updates since:`, checkTime, 'Last known:', lastMessageTimestampRef.current);
        const updates = await conversationService.getNewMessages(sessionId, checkTime);
        console.log(`[Session: ${sessionId}] Message updates response:`, updates);
        if (updates.messages && updates.messages.length > 0) {
          console.log(`[Session: ${sessionId}] Found new messages:`, updates.messages.length);
          console.log(`[Session: ${sessionId}] New messages:`, updates.messages);
          // Add only new messages
          addNewMessages(updates.messages);
        } else {
          console.log(`[Session: ${sessionId}] No new messages found`);
        }
      } catch (error) {
        console.error('Error checking for message updates:', error);
      }
    }, 3000); // Chequea cada 3 segundos
    
    // Cleanup del intervalo
    return () => {
      clearInterval(interval);
      console.log('Cleaned up message update interval for session:', sessionId);
      // Limpiar el timestamp cuando cambiamos de conversación
      lastMessageTimestampRef.current = null;
    };
  }, [sessionId, isPhoneMode]); // Depende de sessionId y isPhoneMode

  // Effect to auto-scroll when new messages arrive and the user is near the end
  useEffect(() => {
    if (!conversation || conversation.messages.length === 0) return;
    
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      const distanceFromBottom = scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
      
      // Si está cerca del final (menos de 300px), hacer auto-scroll
      if (distanceFromBottom < 300) {
        console.log('Auto-scrolling because user is near bottom:', distanceFromBottom);
        setTimeout(() => {
          bottomRef.current?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'end'
          });
        }, 100);
      }
    }
  }, [conversation?.messages?.length]); // Executes when the number of messages changes

  const loadConversation = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      // Guardar la posición actual del scroll antes de actualizar
      const scrollElement = scrollRef.current;
      const distanceFromBottom = scrollElement 
        ? scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight
        : 0;
      const wasNearBottom = distanceFromBottom < 300;
      
      let data;
      // Check if sessionId is a phone number (contains only digits, +, and possibly -)
      const isPhone = /^[\d+\-]+$/.test(decodeURIComponent(sessionId));
      
      if (isPhone) {
        // Load messages by phone number
        setIsPhoneMode(true);
        const phoneNumber = decodeURIComponent(sessionId);
        data = await conversationService.getMessagesByPhone(phoneNumber);
        
        // Transform phone-based data to match conversation format
        if (data.sessions && data.sessions.length > 0) {
          // Flatten all messages from all sessions
          const allMessages = [];
          data.sessions.forEach(session => {
            session.messages.forEach(msg => {
              allMessages.push(msg);
            });
          });
          
          // Sort by timestamp
          allMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          
          data = {
            participant_phone: phoneNumber,
            messages: allMessages,
            message_count: data.total_messages,
            session_count: data.total_sessions,
            is_answered: data.is_answered,
            sessions: data.sessions // Keep sessions for display
          };
        } else {
          // No sessions found, create empty structure
          data = {
            participant_phone: phoneNumber,
            messages: [],
            message_count: 0,
            session_count: 0,
            is_answered: false
          };
        }
      } else {
        // Load by session ID (legacy)
        setIsPhoneMode(false);
        data = await conversationService.getDetail(sessionId);
      }
      
      const prevLength = conversation?.messages?.length || 0;
      const newLength = data?.messages?.length || 0;
      const hasNewMessages = newLength > prevLength;
      
      setConversation(data);
      setError(null);
      
      // Save the timestamp of the last message for future updates
      if (data?.messages && data.messages.length > 0) {
        const lastMessage = data.messages[data.messages.length - 1];
        const timestampField = lastMessage.message_timestamp || lastMessage.timestamp;
        if (timestampField) {
          lastMessageTimestampRef.current = timestampField;
          console.log('Updated lastMessageTimestamp to:', timestampField);
        }
      }
      

      
      // Hacer auto-scroll si:
      // 1. Es la primera carga (!silent)
      // 2. O si estaba cerca del final (wasNearBottom)
      // 3. Or if there are new messages and was near the end
      const shouldScroll = !silent || wasNearBottom || (hasNewMessages && wasNearBottom);
      
      if (shouldScroll) {
        setTimeout(() => {
          bottomRef.current?.scrollIntoView({ 
            behavior: silent ? 'smooth' : 'auto',
            block: 'end'
          });
        }, 100);
      }
    } catch (err) {
      if (!silent) {
        setError('Failed to load conversation');
        console.error(err);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Function to add only new messages
  const addNewMessages = useCallback((newMessages) => {
    console.log('addNewMessages called with:', newMessages);
    if (!newMessages || newMessages.length === 0) {
      console.log('No new messages to add');
      return;
    }
    
    setConversation(prevConversation => {
      if (!prevConversation) {
        console.log('No conversation loaded yet - skipping update');
        return prevConversation;
      }
      
      // Filter messages that already exist
      const existingIds = new Set(prevConversation.messages.map(m => m.message_id));
      const trulyNew = newMessages.filter(m => !existingIds.has(m.message_id));
      
      console.log('Filtered to', trulyNew.length, 'truly new messages from', newMessages.length);
      
      if (trulyNew.length === 0) {
        console.log('No truly new messages after filtering');
        return prevConversation;
      }
      
      // Combinar y ordenar por timestamp
      const combinedMessages = [...prevConversation.messages, ...trulyNew];
      const sortedMessages = combinedMessages.sort((a, b) => 
        new Date(a.message_timestamp) - new Date(b.message_timestamp)
      );
      
      // Update the timestamp of the last message
      if (sortedMessages.length > 0) {
        const lastMessage = sortedMessages[sortedMessages.length - 1];
        if (lastMessage.message_timestamp) {
          lastMessageTimestampRef.current = lastMessage.message_timestamp;
          console.log('Updated lastMessageTimestamp to:', lastMessage.message_timestamp);
        }
      }
      
      console.log(`Added ${trulyNew.length} new messages. Total: ${sortedMessages.length}`);
      
      // Verificar si debemos incrementar el contador de no leídos
      // Esto se ejecuta sincrónicamente dentro del setState
      const scrollElement = scrollRef.current;
      if (scrollElement && trulyNew.length > 0) {
        const distanceFromBottom = scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
        console.log('Distance from bottom:', distanceFromBottom);
        
        // Si está lejos del final, incrementar contador
        if (distanceFromBottom > 300) {
          console.log(`User is ${distanceFromBottom}px from bottom, incrementing counter by ${trulyNew.length}`);
          // Usar setTimeout para evitar actualizar estado dentro de setState
          setTimeout(() => {
            setUnreadNewMessages(prev => prev + trulyNew.length);
          }, 0);
        } else {
          console.log(`User is near bottom (${distanceFromBottom}px), auto-scrolling instead`);
          // Si está cerca del final, hacer auto-scroll
          setTimeout(() => {
            bottomRef.current?.scrollIntoView({ 
              behavior: 'smooth',
              block: 'end'
            });
          }, 100);
        }
      }
      
      return {
        ...prevConversation,
        messages: sortedMessages
      };
    });
  }, [sessionId]);



  const handleScroll = (e) => {
    const element = e.target;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    
    // Mostrar botón de scroll si está a más de 300px del fondo
    setShowScrollButton(distanceFromBottom > 300);
    
    // Si está cerca del final (menos de 100px), resetear contador
    if (distanceFromBottom < 100) {
      setUnreadNewMessages(0);
    }
  };
  
  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setUnreadNewMessages(0); // Resetear contador al hacer scroll manual al final
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || sending) return;
    
    setSending(true);
    try {
      // TODO: Implement API to send message
      console.log('Sending message:', messageInput);
      // await conversationService.sendMessage(sessionId, messageInput);
      setMessageInput('');
      // Recargar conversación
      await loadConversation(true);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!sessionId) {
    return (
      <Box 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: '#f0f2f5',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Select a conversation to start
        </Typography>
      </Box>
    );
  }

  if (loading && !conversation) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !conversation) {
    return (
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const phoneInfo = getPhoneCountryInfo(conversation?.participant_phone);

  return (
    <Box sx={{ 
      display: 'flex', 
      flex: '1 1 auto', 
      height: '100vh', 
      overflow: 'hidden', 
      minWidth: 0 
    }}>
      {/* Left: Conversation Area */}
      <Box sx={{ 
        flex: '1 1 auto',
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh',
        minWidth: 0,
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box 
          sx={{ 
            height: 56,
            bgcolor: 'white',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            boxSizing: 'border-box',
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: '#dfe5e7',
              fontSize: '1.3rem',
              mr: 2,
              flexShrink: 0,
            }}
          >
            {phoneInfo.flag}
          </Avatar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                {conversation?.participant_name || phoneInfo.formattedNumber || conversation?.participant_phone}
              </Typography>
              {conversation?.participant_name && (
                <>
                  <Typography variant="body2" sx={{ color: '#667781', fontSize: '0.875rem' }}>
                    |
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#667781', fontSize: '0.875rem' }}>
                    {phoneInfo.formattedNumber || conversation.participant_phone}
                  </Typography>
                </>
              )}
              <Typography variant="caption" sx={{ color: '#667781', fontSize: '0.75rem' }}>
                • {conversation?.messages?.length} {conversation?.messages?.length === 1 ? 'message' : 'messages'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="View Participant Profile">
              <IconButton size="small" onClick={() => setIsSiderOpen(true)}>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            overflowX: 'hidden',
            bgcolor: '#efeae2',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4d4d4' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            p: 2,
            position: 'relative',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            {conversation?.messages?.map((message, index) => {
              // Show date divider if it's the first message or if the date changed from previous message
              const messageTimestamp = message.message_timestamp || message.timestamp;
              const currentDate = formatDateArgentina(messageTimestamp);
              const previousDate = index > 0 ? formatDateArgentina(conversation.messages[index - 1].message_timestamp || conversation.messages[index - 1].timestamp) : null;
              const showDateDivider = index === 0 || currentDate !== previousDate;
              
              // For phone mode, also show session dividers
              const showSessionDivider = isPhoneMode && index > 0 && 
                message.session_id !== conversation.messages[index - 1].session_id;
              
              return (
                <MessageBubble 
                  key={index}
                  message={message} 
                  showDateDivider={showDateDivider}
                  showSessionDivider={showSessionDivider}
                />
              );
            })}
            <div ref={bottomRef} />
          </Box>
        </Box>
        
        {/* Scroll to bottom button - Fixed position relative to viewport */}
        {showScrollButton && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 76, // Justo encima del input box
              right: 24,
              zIndex: 1200,
            }}
          >
            <Badge 
              badgeContent={unreadNewMessages} 
              color="primary"
              overlap="circular"
              invisible={unreadNewMessages === 0}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#25D366', // Color verde de WhatsApp
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  minWidth: '20px',
                  height: '20px',
                  padding: '0 4px',
                }
              }}
            >
              <IconButton
                onClick={scrollToBottom}
                size="medium"
                sx={{
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: '#f0f0f0',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  },
                  border: '1px solid rgba(0,0,0,0.08)',
                  width: 42,
                  height: 42,
                }}
              >
                <ArrowDownIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Badge>
          </Box>
        )}

        {/* Input Box */}
        <Box 
          sx={{ 
            minHeight: 56,
            maxHeight: 56,
            bgcolor: '#f0f2f5',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            gap: 1,
            boxSizing: 'border-box',
          }}
        >
          <IconButton size="small" sx={{ color: '#54656f', p: 0.75 }}>
            <EmojiIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton size="small" sx={{ color: '#54656f', p: 0.75 }}>
            <AttachFileIcon sx={{ fontSize: 20 }} />
          </IconButton>
          
          <TextField
            fullWidth
            multiline
            maxRows={1}
            placeholder="Type a message"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                borderRadius: 2,
                minHeight: 36,
                maxHeight: 36,
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
              },
              '& .MuiInputBase-input': {
                py: 0.5,
                fontSize: '0.9rem',
              },
            }}
          />
          
          {messageInput.trim() ? (
            <IconButton 
              onClick={handleSendMessage}
              disabled={sending}
              size="small"
              sx={{ 
                color: '#00a884',
                p: 0.75,
                '&:hover': {
                  bgcolor: 'rgba(0, 168, 132, 0.1)',
                },
              }}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : (
            <IconButton size="small" sx={{ color: '#54656f', p: 0.75 }}>
              <MicIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Right: Sider */}
      {isSiderOpen && (
        <ParticipantSider 
          phoneNumber={conversation?.participant_phone}
          onClose={() => setIsSiderOpen(false)}
        />
      )}
    </Box>
  );
};

export default ConversationDetailNew;
